import React, { useEffect, useState, useRef, useCallback } from "react";
import {
  Card,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Container,
  CardContent,
  Button,
  Grid,
  Stack,
  Skeleton,
  Menu,
  MenuItem,
  IconButton,
} from "@material-ui/core";
import { experimentalStyled as styled } from "@material-ui/core/styles";
import { makeStyles } from "@material-ui/styles";
import LoadingButton from "@material-ui/lab/LoadingButton";
import DownloadIcon from "@material-ui/icons/Download";
import CancelIcon from "@material-ui/icons/Cancel";
import MoreVertIcon from "@material-ui/icons/MoreVert";
import PagosFacturaDialog from "./PagosFacturaDialog";
import FacturarDialog from "./FacturarDialog";
import { useSelector } from "react-redux";
import { AlertaModalV2 } from "../AlertaSwall";
import { dteApi } from "src/services/dteApi";
import Label from "../Label";
import { sentenceCase } from "change-case";
import {
  obtenerValorMoneda,
  descargarPdfDteProxy,
} from "src/utils/sharedFunctions";
import { facturasApi } from "src/services/facturasApi";
import { formatearFecha } from "src/utils/formatTime";
import ValidacionPagoDialog from "./ValidacionDialog";
import { useSnackbar } from "src/components/snackbar";
import InformacionValidacionModal from "./InformacionValidacionModal";

// ----------------------------------------------------------------------

const RootStyle = styled("div")(({ theme }) => ({
  marginTop: "1rem",
}));

const useStyles = makeStyles({
  table: {
    minWidth: 650,
  },
  hidden: {
    display: "none",
  },
});

// ----------------------------------------------------------------------

export default function FacturasTarea({ tarea, monedas, refetchTarea }) {
  const classes = useStyles();
  const { sucursal: sucursalObtenida, rol: rolObtenido } = useSelector(
    (state) => state.auth.user,
  );

  const {
    data: facturas = [],
    isLoading: obteniendoFacturasTarea,
    refetch: recargarFacturas,
  } = facturasApi.useGetFacturaTareasIDQuery({
    refTarea: tarea.id,
    sucursal: sucursalObtenida.refSucursal,
  });

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [mostrarPagos, setMostrarPagos] = useState(false);
  const [loadingDescargar, setLoadingDescargar] = useState(null);

  const [facturaSeleccionada, setFacturaSeleccionada] = useState(null);

  const [modalFactura, setModalFactura] = useState(false);
  const [modalValidacion, setModalValidacion] = useState(false);
  const [facturaAValidar, setFacturaAValidar] = useState(null);
  const [totalFacturado, setTotalFacturado] = useState(0);
  const [valorTareaPesos, setValorTareaPesos] = useState(0);

  const [modalInfoValidacion, setModalInfoValidacion] = useState(false);
  const [facturaDetalleValidacion, setFacturaDetalleValidacion] =
    useState(null);

  // Estados para el menú
  const [anchorEl, setAnchorEl] = useState(null);
  const [filaMenuAbierto, setFilaMenuAbierto] = useState(null);

  const [crearDTE] = dteApi.useCrearDTEMutation();
  const [descargarPdfDte] = dteApi.useDescargarPdfDteMutation();

  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    if (isMounted.current && monedas) {
      const { tipoMonedaTarea, valorTarea } = tarea;
      const valorTareaEnPesos =
        tipoMonedaTarea === "PESOS"
          ? valorTarea
          : obtenerValorMoneda(monedas, tipoMonedaTarea) * valorTarea;

      if (valorTareaEnPesos === null || valorTareaEnPesos === undefined) {
        console.error("Error al obtener las monedas");
        throw Error("Error al obtener las monedas");
      }

      setValorTareaPesos(valorTareaEnPesos);
    }
  }, [monedas, tarea]);

  function formatNumber(n) {
    n = n > 0 ? String(n).replace(/\D/g, "") : n;
    return n === "" ? n : Number(n).toLocaleString("es-CL");
  }

  const calcularTotalFacturasEmitidas = useCallback(() => {
    const sumFacturasEmitidas = tarea.facturas.reduce((total, factura) => {
      if (
        factura.estadoFactura !== "ANULADO" &&
        factura.estadoFactura !== "INCOBRABLE"
      ) {
        return total + factura.total;
      }
      return total;
    }, 0);

    setTotalFacturado(sumFacturasEmitidas);
  }, [tarea.facturas]);

  const handleGenerarDocumento = useCallback(() => {
    setModalFactura(true);
  }, []);

  useEffect(() => {
    calcularTotalFacturasEmitidas();
  }, [calcularTotalFacturasEmitidas]);

  // Funciones del menú
  const handleMenuClick = (event, rowIndex) => {
    setAnchorEl(event.currentTarget);
    setFilaMenuAbierto(rowIndex);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setFilaMenuAbierto(null);
  };

  // Función para abrir modal de validación
  const abrirModalValidacion = (factura) => {
    setFacturaAValidar(factura);
    setModalValidacion(true);
    handleMenuClose();
  };

  const anularFactura = useCallback(
    async (row) => {
      handleMenuClose();
      const result = await AlertaModalV2({
        title: "¿Anular el documento?",
        confirmButtonText: "Sí, anular",
      }).fire();
      if (!result?.isConfirmed) {
        return;
      }
      const loadingKey = enqueueSnackbar("Anulando factura...", {
        variant: "info",
        persist: true,
      });
      try {
        await crearDTE({
          montoFactura: row.total,
          tipoDTE: 61,
          tokenAnular: row.token,
          tipoFactura: "TAREA",
          formatoPago: 2,
          montoPagado: "SI",
          metodoPago: "EFECTIVO",
          idTarea: row.refTarea,
          sucursal: row.sucursal,
        }).unwrap();

        closeSnackbar(loadingKey);
        enqueueSnackbar(`Factura ${row.folio} anulada correctamente`, {
          variant: "success",
        });

        recargarFacturas();
      } catch (error) {
        console.error("Error al anular la factura:", error);
        closeSnackbar(loadingKey);
        enqueueSnackbar(`Error al anular la factura ${row.folio}`, {
          variant: "error",
        });
      }
    },
    [closeSnackbar, crearDTE, enqueueSnackbar, recargarFacturas],
  );

  const abrirModalInformacion = (factura) => {
    setFacturaDetalleValidacion(factura);
    setModalInfoValidacion(true);
  };

  // Descarga el PDF por el proxy backend (la apikey vive solo en el backend).
  const descargarPDF = useCallback(
    async (row) => {
      setLoadingDescargar(row.folio);
      try {
        await descargarPdfDteProxy({
          trigger: descargarPdfDte,
          tipo: "tarea",
          id: row.id,
          nombreArchivo: `${row.folio}.pdf`,
          enqueueSnackbar,
        });
      } finally {
        setLoadingDescargar(null);
      }
    },
    [descargarPdfDte, enqueueSnackbar],
  );

  return (
    <Container>
      <RootStyle>
        <Card style={{ padding: "1rem" }}>
          <CardContent>
            <Grid container>
              <Grid item xs={6} md={6}>
                <Button
                  variant="contained"
                  color="primary"
                  disabled={
                    totalFacturado >= valorTareaPesos ||
                    tarea.estado === "FACTURADO"
                  }
                  onClick={handleGenerarDocumento}
                >
                  Generar Documento Tributario
                </Button>
              </Grid>
              <Grid item xs={6} md={6}></Grid>
            </Grid>

            <TableContainer component={Paper} sx={{ mt: 2 }}>
              {!obteniendoFacturasTarea ? (
                <Table className={classes.table} size="small">
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "grey.50" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Folio</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Total Facturado
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Estado Factura
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Estado Correo
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Fecha Facturación
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Empleado Envía Correo
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Estado Validación
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>
                        Estado Pago empleado
                      </TableCell>
                      <TableCell sx={{ fontWeight: "bold", minWidth: 180 }}>
                        Acciones
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {facturas &&
                      facturas.map((row, idx) => {
                        return (
                          <TableRow key={"facturasAsociadas_" + idx} hover>
                            <TableCell component="th" scope="row">
                              {row.folio}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              ${formatNumber(row.total)}
                            </TableCell>
                            <TableCell>
                              <Label
                                variant="ghost"
                                color={
                                  row.estadoFactura === "FACTURADO"
                                    ? "success"
                                    : row.estadoFactura === "PAGADO"
                                      ? "info"
                                      : row.estadoFactura === "ABONO"
                                        ? "warning"
                                        : "error"
                                }
                              >
                                {sentenceCase(row.estadoFactura)}
                              </Label>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Label
                                variant="ghost"
                                color={
                                  (row.estadoCorreo === "ENVIADO" &&
                                    "success") ||
                                  "error"
                                }
                              >
                                {sentenceCase(
                                  row.estadoCorreo === ""
                                    ? "No enviado"
                                    : row.estadoCorreo,
                                )}
                              </Label>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {formatearFecha(
                                row.fechaFacturacion,
                                "DD/MM/YYYY",
                              )}
                            </TableCell>
                            <TableCell component="th" scope="row">
                              {row.empleadoEnviaCorreo || "-"}
                            </TableCell>
                            <TableCell>
                              <Label
                                variant="ghost"
                                color={
                                  row.estadoValidacionSupervisor === "APROBADO"
                                    ? "success"
                                    : row.estadoValidacionSupervisor ===
                                        "RECHAZADO"
                                      ? "error"
                                      : row.estadoValidacionSupervisor ===
                                          "NO_APLICA"
                                        ? "default"
                                        : "warning"
                                }
                              >
                                {sentenceCase(
                                  row.estadoValidacionSupervisor || "PENDIENTE",
                                )}
                              </Label>
                            </TableCell>
                            <TableCell>
                              <Label
                                variant="ghost"
                                color={
                                  row.estadoPagoEmpleado === "PAGADO"
                                    ? "success"
                                    : row.estadoPagoEmpleado === "NO_APLICA"
                                      ? "default"
                                      : "warning"
                                }
                              >
                                {sentenceCase(
                                  row.estadoPagoEmpleado || "PENDIENTE",
                                )}
                              </Label>
                            </TableCell>
                            <TableCell component="th" scope="row">
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                              >
                                {/* Botón principal de descarga */}
                                <LoadingButton
                                  size="small"
                                  variant="outlined"
                                  color="primary"
                                  loading={loadingDescargar === row.folio}
                                  disabled={loadingDescargar === row.folio}
                                  onClick={() => descargarPDF(row)}
                                  startIcon={<DownloadIcon />}
                                >
                                  PDF
                                </LoadingButton>

                                {/* Botón de validación prominente para supervisores */}
                                {(rolObtenido === "SUPERVISOR" ||
                                  rolObtenido === "ADMIN" ||
                                  rolObtenido === "SUPER_ADMIN") &&
                                  row.estadoValidacionSupervisor ===
                                    "PENDIENTE" &&
                                  !["ANULADO", "INCOBRABLE"].includes(
                                    row.estadoFactura,
                                  ) && (
                                    <LoadingButton
                                      size="small"
                                      variant="contained"
                                      color="warning"
                                      onClick={() => abrirModalValidacion(row)}
                                    >
                                      Validar
                                    </LoadingButton>
                                  )}

                                {/* Menú de más opciones */}
                                <IconButton
                                  size="small"
                                  onClick={(e) => handleMenuClick(e, idx)}
                                >
                                  <MoreVertIcon />
                                </IconButton>

                                <Menu
                                  anchorEl={anchorEl}
                                  open={filaMenuAbierto === idx}
                                  onClose={handleMenuClose}
                                  PaperProps={{
                                    sx: { minWidth: 140 },
                                  }}
                                >
                                  <MenuItem
                                    onClick={() => {
                                      setFacturaSeleccionada(row);
                                      setMostrarPagos(true);
                                      handleMenuClose();
                                    }}
                                  >
                                    Ver Pagos
                                  </MenuItem>

                                  <MenuItem
                                    onClick={() => {
                                      handleMenuClose();
                                      abrirModalInformacion(row);
                                    }}
                                  >
                                    Ver detalle validación
                                  </MenuItem>

                                  {row.estadoFactura === "FACTURADO" &&
                                    ![
                                      "EMPLEADO",
                                      "SUPERVISOR",
                                      "ADMIN_OPERACIONES",
                                    ].includes(rolObtenido) && (
                                      <MenuItem
                                        onClick={() => anularFactura(row)}
                                        sx={{ color: "error.main" }}
                                      >
                                        <CancelIcon
                                          sx={{ mr: 1, fontSize: 16 }}
                                        />
                                        Anular Factura
                                      </MenuItem>
                                    )}
                                </Menu>
                              </Stack>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
              ) : (
                <Skeleton variant="rectangular" width="100%" height="300px" />
              )}
            </TableContainer>
          </CardContent>
        </Card>

        {/* Dialogs */}
        {mostrarPagos && facturaSeleccionada && (
          <PagosFacturaDialog
            open={mostrarPagos}
            setOpen={setMostrarPagos}
            factura={facturaSeleccionada}
          />
        )}

        {modalInfoValidacion && (
          <InformacionValidacionModal
            open={modalInfoValidacion}
            onClose={() => setModalInfoValidacion(false)}
            factura={facturaDetalleValidacion}
          />
        )}

        {modalFactura && (
          <FacturarDialog
            open={modalFactura}
            setOpen={setModalFactura}
            tarea={tarea}
            monedas={monedas}
            setTotalFacturadoEntrada={setTotalFacturado}
            setValorTareaPesos={setValorTareaPesos}
            recargarFacturas={recargarFacturas}
          />
        )}

        <ValidacionPagoDialog
          open={modalValidacion}
          onClose={() => setModalValidacion(false)}
          factura={facturaAValidar}
          tarea={tarea}
        />
      </RootStyle>
    </Container>
  );
}
